import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, concat, forkJoin, Observable, of, Subject, takeUntil } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';
import { ToastService } from '../../../../shared/services/toast.service';
import { ContactModel } from '../../../contact/models/contact.model';
import { ContactRepository } from '../../../contact/states/contact.repository';
import { AnimalModel } from '../../models/animal.model';
import {
  AnimalVeterinaryContactModel,
  AnimalVeterinaryContactUpdatePayload,
  VETERINARY_CONTACT_TYPE
} from '../../models/medical/animal-veterinary-contact.model';
import { formatVeterinarianName } from '../../models/medical/medical.helpers';
import { refId } from '../../models/medical/medical-ref.model';
import { AnimalMedicalRepository } from '../../states/medical/animal-medical.repository';
import { AnimalVeterinaryContactRepository } from '../../states/medical/animal-veterinary-contact.repository';

interface VeterinaryEntry {
  link: AnimalVeterinaryContactModel;
  contact: ContactModel | null;
}

interface EditRow {
  id: number | null;
  contactId: number;
  label: string;
  isPrimary: boolean;
}

/** Haustierarzt: Stammdatum am Tier, nicht Teil der Karteikarte. */
@Component({
  selector: 'app-medical-veterinary-card',
  templateUrl: './medical-veterinary-card.component.html',
  standalone: false
})
export class MedicalVeterinaryCardComponent implements OnInit, OnDestroy {
  @Input() animal: AnimalModel | null = null;

  primary: VeterinaryEntry | null = null;
  others: VeterinaryEntry[] = [];

  dialogVisible = false;
  saving = false;
  rows: EditRow[] = [];
  addContactId: number | null = null;
  /** Bereits verknüpfte Tierärzte werden in der Auswahl nicht erneut angeboten. */
  excludedContactIds: number[] = [];

  private _links: AnimalVeterinaryContactModel[] = [];
  private _contactsById: Record<number, ContactModel> = {};
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _medicalRepository: AnimalMedicalRepository,
    private readonly _veterinaryContactRepository: AnimalVeterinaryContactRepository,
    private readonly _contactRepository: ContactRepository,
    private readonly _toast: ToastService,
    private readonly _translateService: TranslocoService
  ) {}

  ngOnInit(): void {
    combineLatest([this._medicalRepository.veterinaryContacts$, this._contactRepository.selectById$()])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([links, byId]) => {
        this._links = links;
        this._contactsById = byId;
        const entries = links.map(link => ({ link, contact: byId[refId(link.contact) ?? -1] ?? null }));
        this.primary = entries.find(entry => entry.link.isPrimary) ?? null;
        this.others = entries.filter(entry => entry !== this.primary);
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  name(contact: ContactModel | null): string {
    return formatVeterinarianName(contact) || '–';
  }

  personName(contact: ContactModel | null): string {
    if (!contact?.companyName) return '';
    return [contact.title, contact.firstName, contact.lastName].filter(Boolean).join(' ');
  }

  phone(contact: ContactModel | null): string | null {
    if (!contact) return null;
    const numbers = contact.phoneNumbers ?? [];
    const primary = numbers.find(entry => entry.isPrimary && entry.number) ?? numbers.find(entry => !!entry.number);
    return primary?.number ?? contact.phoneNumber ?? contact.mobilePhoneNumber ?? null;
  }

  address(contact: ContactModel | null): string {
    if (!contact) return '';
    return [contact.street, [contact.zipCode, contact.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  }

  // --- Pflegedialog ---------------------------------------------------------------------------

  openDialog(): void {
    this.rows = this._links.map(link => ({
      id: link.id,
      contactId: refId(link.contact)!,
      label: link.label ?? '',
      isPrimary: link.isPrimary,
    }));
    this.updateExcluded();
    this.addContactId = null;
    this.dialogVisible = true;
  }

  closeDialog(): void {
    this.dialogVisible = false;
  }

  rowName(row: EditRow): string {
    return this.name(this._contactsById[row.contactId] ?? null);
  }

  onAddContact(contactId: number | null): void {
    if (!contactId || this.rows.some(row => row.contactId === contactId)) return;
    this.rows = [...this.rows, { id: null, contactId, label: '', isPrimary: this.rows.length === 0 }];
    this.updateExcluded();
    // erst nach dem Change-Detection-Zyklus leeren, damit die Auswahl zurückgesetzt wird
    setTimeout(() => this.addContactId = null);
  }

  setPrimary(index: number): void {
    this.rows = this.rows.map((row, i) => ({ ...row, isPrimary: i === index }));
  }

  removeRow(index: number): void {
    const removedPrimary = this.rows[index].isPrimary;
    this.rows = this.rows.filter((_, i) => i !== index);
    this.updateExcluded();
    if (removedPrimary && this.rows.length) {
      this.setPrimary(0);
    }
  }

  private updateExcluded(): void {
    this.excludedContactIds = this.rows.map(row => row.contactId);
  }

  save(): void {
    const animalId = this.animal?.id;
    if (!animalId || this.saving) return;

    const originals = new Map(this._links.map(link => [link.id, link]));
    const keptIds = new Set(this.rows.filter(row => row.id !== null).map(row => row.id!));

    const deletes = this._links
      .filter(link => !keptIds.has(link.id))
      .map(link => this._veterinaryContactRepository.delete(link.id, true));

    // Bisherigen Haupt-Tierarzt zuerst herabstufen, damit es nie zwei gibt.
    const demotions: Observable<unknown>[] = [];
    const updates: Observable<unknown>[] = [];
    for (const row of this.rows) {
      if (row.id === null) continue;
      const original = originals.get(row.id)!;
      const changes: AnimalVeterinaryContactUpdatePayload = {};
      if ((original.label ?? '') !== row.label) changes.label = row.label || null;
      if (original.isPrimary !== row.isPrimary) changes.isPrimary = row.isPrimary;
      if (!Object.keys(changes).length) continue;

      const request = this._veterinaryContactRepository.update(row.id, changes, true);
      (original.isPrimary && !row.isPrimary ? demotions : updates).push(request);
    }

    const creates = this.rows
      .filter(row => row.id === null)
      .map(row => this._veterinaryContactRepository.create({
        animal: { id: animalId },
        contact: { id: row.contactId },
        type: VETERINARY_CONTACT_TYPE,
        label: row.label || null,
        isPrimary: row.isPrimary,
      }, true));

    const phase = (requests: Observable<unknown>[]) =>
      requests.length ? forkJoin(requests) : of([] as unknown[]);

    this.saving = true;
    let failed = false;
    concat(phase([...deletes, ...demotions]), phase([...updates, ...creates]))
      .pipe(
        map(results => results.some(result => result === null || result === false)),
        finalize(() => this.saving = false)
      )
      .subscribe({
        next: phaseFailed => failed = failed || phaseFailed,
        complete: () => {
          this._medicalRepository.reloadVeterinaryContacts().pipe(take(1)).subscribe();
          if (failed) {
            this._toast.showError(this._translateService.translate('animalMedical.toast.veterinaryContact.saveError'));
          } else {
            this._toast.showSuccess(this._translateService.translate('toast.saveSuccess'));
            this.dialogVisible = false;
          }
        }
      });
  }
}
